import { Box, Flex, Heading, Menu, MenuButton, MenuList, Skeleton } from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useResources } from 'datx-jsonapi-react';
import NextImage from 'next/image';
import { useRouter } from 'next/router';
import { FC, Fragment } from 'react';

import { SecondaryMenuDesktop } from '@/components/ui/shared/headers/EventHeader/layouts/SecondaryMenu.desktop';
import { SecondaryMenuMobile } from '@/components/ui/shared/headers/EventHeader/layouts/SecondaryMenu.mobile';
import { sessionQueryOptions } from '@/queries/upgrade/sessions';
import { EventTicket } from '@/resources/EventTicket';
import { ThinEvent } from '@/resources/ThinEvent';
import { AnyProfile } from '@/utils/profileHelpers';

import { DesktopUserMenuItems } from '../DesktopUserMenuItems/DesktopUserMenuItems';
import { ProfileIcon } from '../ProfileIcon/ProfileIcon';

// The right-hand side of the header: event branding, the ticket-gated secondary menu, and the
// profile dropdown. Ticket ownership decides whether the secondary menu renders at all.
export const HorizontalBar: FC<{
  event: ThinEvent;
  currentUserProfile: AnyProfile;
  onLogout: () => void;
}> = ({ event, currentUserProfile, onLogout }) => {
  const router = useRouter();
  const { data: session } = useQuery(sessionQueryOptions());
  const user = session?.user;

  const { data: tickets, error: ticketsError } = useResources(() =>
    user?.id && event?.id
      ? [
          EventTicket,
          {
            filter: {
              event_id: event.id,
              user_id: user.id,
            },
          },
        ]
      : null
  );

  const isTicketsLoading = !tickets && !ticketsError;
  const isUserWithTicket = !isTicketsLoading && user && tickets?.length > 0;

  return (
    <Fragment>
      <Box
        w={{ base: 8, md: 10, lg: 12 }}
        h={{ base: 8, md: 10, lg: 12 }}
        px={{ base: 5, md: 4, lg: 3 }}
        py={{ base: 5, md: 4, lg: 3 }}
        pl={{ base: 0, lg: 3 }}
        boxSizing="content-box"
      >
        {event?.smallLogoUrl && <NextImage width={48} height={48} src={event.smallLogoUrl} alt={event.name} />}
        {!event?.smallLogoUrl && <Skeleton w={12} h={12} />}
      </Box>

      <Heading
        as="h1"
        noOfLines={1}
        minW="0"
        mx={{ md: 4, lg: 8, xl: 10 }}
        fontSize={{ base: 'md', md: 'xl' }}
        lineHeight={{ base: 'base', md: 1.4, lg: 1.2 }}
      >
        {event?.name || <Skeleton h={{ base: '1.5rem', md: '1.75rem', lg: '1.5rem' }} w="10.5rem" />}
      </Heading>

      {isUserWithTicket && (
        <SecondaryMenuMobile ml="auto" display={{ base: 'block', lg: 'none' }} key={router.asPath} />
      )}

      <Flex ml="auto" mr="5" display={{ base: 'none', lg: 'inherit' }} alignItems="center">
        {isUserWithTicket && <SecondaryMenuDesktop display={{ base: 'none', lg: 'flex' }} key={router.asPath} />}
        <Menu variant="navigation" placement="bottom-end">
          <MenuButton
            ml="5"
            h={8}
            w={8}
            display="flex"
            justifyContent="center"
            alignItems="center"
            aria-label="Profile button"
            p={1}
            _hover={{
              bg: 'gray.200',
              boxShadow: '0 0 0 0.625rem #CACCCC',
            }}
            _focus={{
              bg: 'gray.200',
              boxShadow: '0 0 0 0.625rem #CACCCC !important',
            }}
          >
            <ProfileIcon profile={currentUserProfile} size={24} />
          </MenuButton>
          <MenuList>
            <DesktopUserMenuItems
              user={user}
              currentUserProfile={currentUserProfile}
              redirectUrl={router.asPath}
              onLogout={onLogout}
            />
          </MenuList>
        </Menu>
      </Flex>
    </Fragment>
  );
};
